/**
 * One dialog in sight at a time.
 *
 * Several flows open a dialog from inside another: Invest opens the order
 * summary, the summary opens the one-time code, a top-up or a withdrawal
 * does the same. Each was drawn on top of the one that opened it — two
 * scrims, two headers, the form's Invest still showing around the summary's
 * edges. The newest dialog is the step the reader is on, so it is the only
 * one shown; backing out of it brings the one beneath back, the way a step
 * back would.
 *
 * It is watched for rather than wired into each dialog, because dialogs in
 * this app are built three ways — the shared shells, styled Ant Design
 * modals, and Ant Design's own confirm — and a rule that every caller has to
 * remember is the rule the next dialog forgets.
 *
 * Hidden, not removed from the layout: a dialog beneath keeps its state, and
 * one that is closing still plays its close animation, out of sight. Coming
 * back waits for the top dialog's close animation, so a dialog that the top
 * one's success closes as well does not flash on screen on its way out.
 */
export const RESTORE_DELAY_MS = 300;

const ROOT = ".ant-modal-root";

/* Ant Design hides a closed dialog's wrap once its close animation has
   played, and marks the dialog itself `-leave` while it plays. Either way
   the reader is no longer on it. */
const isOpen = (root: HTMLElement) => {
  const wrap = root.querySelector<HTMLElement>(".ant-modal-wrap");
  if (!wrap || wrap.style.display === "none") return false;
  const dialog = wrap.querySelector<HTMLElement>(".ant-modal");
  return !!dialog && !/-leave(\s|$)/.test(dialog.className);
};

const isGone = (root: HTMLElement, doc: Document) => {
  const wrap = root.querySelector<HTMLElement>(".ant-modal-wrap");
  return !doc.body.contains(root) || !wrap || wrap.style.display === "none";
};

export const watchDialogs = (doc: Document = document) => {
  const view = doc.defaultView;
  if (!view || typeof view.MutationObserver === "undefined") return () => {};

  /* In the order they opened; the last is the one the reader is on. */
  let opened: HTMLElement[] = [];
  const covered = new Set<HTMLElement>();
  let pending: ReturnType<typeof setTimeout> | undefined;

  const cover = (root: HTMLElement) => {
    if (covered.has(root)) return;
    covered.add(root);
    root.style.visibility = "hidden";
  };

  const uncover = (root: HTMLElement) => {
    if (!covered.delete(root)) return;
    root.style.visibility = "";
  };

  const sync = () => {
    const open = Array.from(doc.querySelectorAll<HTMLElement>(ROOT)).filter(
      isOpen,
    );
    const before = opened[opened.length - 1];
    opened = [
      ...opened.filter((root) => open.includes(root)),
      ...open.filter((root) => !opened.includes(root)),
    ];
    const top = opened[opened.length - 1];

    opened.forEach((root) => root !== top && cover(root));
    /* A dialog that was covered and is now gone never comes back. */
    Array.from(covered)
      .filter((root) => isGone(root, doc))
      .forEach(uncover);

    if (top && covered.has(top) && top !== before) {
      /* The dialog above it has just closed; let that finish first. */
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => {
        pending = undefined;
        if (opened[opened.length - 1] === top && isOpen(top)) uncover(top);
        else sync();
      }, RESTORE_DELAY_MS);
    }
  };

  const observer = new view.MutationObserver((mutations) => {
    const touchesDialog = mutations.some(
      (m) =>
        m.type === "childList" ||
        (m.target instanceof view.Element && !!m.target.closest(ROOT)),
    );
    if (touchesDialog) sync();
  });
  observer.observe(doc.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style"],
  });
  sync();

  return () => {
    observer.disconnect();
    if (pending) clearTimeout(pending);
    Array.from(covered).forEach(uncover);
  };
};
